import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useClipboard } from "@dreamhub/designsystem";
import { cn } from "@/utils/utils";
import { commonRules } from "@/utils/validation";
import Input from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from "@/components/ui/popover";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import { Delete, Link as LinkIcon } from "@dreamhub/designsystem/icons";
import { Link, ExternalLink, Copy, Check } from "lucide-react";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";

const COPIED_FEEDBACK_MS = 2000;

interface ClickableUrlFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  alwaysShowEditButton?: boolean;
}

const ClickableUrlField: React.FC<ClickableUrlFieldProps> = ({
  value,
  onChange,
  placeholder,
  readOnly = false,
  alwaysShowEditButton = false
}) => {
  const { t } = useTranslation();
  const isNewFieldsUI = useNewFieldsUI();
  const [isOpen, setIsOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [touched, setTouched] = useState(false);

  const isValidUrl = (urlString: string): boolean => {
    if (!urlString.trim()) return false;
    const urlValidator = commonRules.url();
    return urlValidator.validate(urlString) === true;
  };

  // Only surfaced once the field has been edited, so opening the popover on an
  // empty value doesn't greet the user with an error.
  const urlError =
    touched && linkUrl.trim() && !isValidUrl(linkUrl)
      ? t("validation.invalidUrl", { defaultValue: "Please enter a valid URL" })
      : null;

  // The value is stored as a plain URL.
  const parsedLink = useMemo(() => {
    if (!value || !value.trim()) return null;
    return { url: value.trim() };
  }, [value]);

  const handleLinkClick = (url: string) => {
    const fullUrl =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `https://${url}`;
    if (!isValidUrl(fullUrl)) return;
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  const handleSave = () => {
    if (!isValidUrl(linkUrl)) return;
    onChange(linkUrl);
    handleCancel();
  };

  const handleCancel = () => {
    setLinkUrl("");
    setTouched(false);
    setIsOpen(false);
  };

  const handlePopoverOpen = (open: boolean) => {
    if (open && parsedLink) {
      // Auto-populate with the existing link
      setLinkUrl(parsedLink.url);
    }
    if (!open) {
      setTouched(false);
    }
    setIsOpen(open);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTouched(true);
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  // navigator.clipboard is absent in a non-secure context, so reading
  // .writeText off it throws synchronously — before any .catch is attached.
  // useClipboard performs the access inside its own try/catch, and keeps the
  // same copied-state timing.
  const { hasCopied, onCopy } = useClipboard(parsedLink?.url ?? "", {
    timeout: COPIED_FEEDBACK_MS
  });

  // Copy whatever is displayed; only opening needs a valid URL.
  const handleCopy = () => {
    if (!parsedLink) return;
    void onCopy();
  };

  // The link and its copy/open actions, shared by the editable row and the
  // read-only one. Each handler stops propagation so that, inside the editable
  // row's popover trigger, pressing one does not also toggle the popover.
  // A real button so a keyboard user can focus and open the link; the
  // editable row's own Enter/Space handler ignores keys from its children.
  const renderLink = (url: string) => (
    <button
      type="button"
      data-test="clickable-url-link"
      onClick={(clickEvent) => {
        clickEvent.stopPropagation();
        clickEvent.preventDefault();
        handleLinkClick(url);
      }}
      className="group/url inline-flex max-w-full text-left hover:underline transition-colors cursor-pointer"
    >
      <Text
        as="span"
        variant="body"
        className={cn(
          "truncate transition-colors",
          // On the Text, not on the span above: `body` sets
          // text-primary on the element itself, and an element's own
          // colour beats an inherited one, so the link rendered as
          // plain body text that merely underlined on hover. The
          // legacy path puts a bare span here, which inherits and is
          // why that one looks right.
          "text-link group-hover/url:text-link-hover",
          alwaysShowEditButton ? "max-w-full" : "max-w-[200px]"
        )}
      >
        {url}
      </Text>
    </button>
  );

  // group-focus-within as well as group-hover: these are real buttons in the
  // tab order, so without it a keyboard user lands on one that is invisible.
  const renderLinkActions = (url: string) => (
    <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
      <IconButton
        variant="icon"
        size="md"
        type="button"
        iconColor="secondary"
        aria-label={hasCopied ? t("copyLink.success") : t("copyTooltip")}
        tooltipText={hasCopied ? t("copied") : t("copyTooltip")}
        data-test="clickable-url-copy"
        onClick={(clickEvent) => {
          clickEvent.stopPropagation();
          clickEvent.preventDefault();
          handleCopy();
        }}
      >
        {hasCopied ? <Check /> : <Copy />}
      </IconButton>
      <IconButton
        variant="icon"
        size="md"
        type="button"
        iconColor="secondary"
        aria-label={t("openLinkTooltip")}
        tooltipText={t("openTooltip")}
        data-test="clickable-url-open"
        onClick={(clickEvent) => {
          clickEvent.stopPropagation();
          clickEvent.preventDefault();
          handleLinkClick(url);
        }}
      >
        <ExternalLink />
      </IconButton>
    </div>
  );

  // The redesign renders a read-only URL as a row without the edit popover,
  // but the link still opens and copies. The pre-redesign rendering keeps the
  // ordinary row and withholds the edit affordance instead — DetailFields
  // passes readOnly for formula and read-only URL fields, so this has to stay
  // behind the flag.
  if (readOnly && isNewFieldsUI) {
    return (
      <div
        data-test="clickable-url-readonly"
        className="group w-full pl-2 pr-[6px] py-1 min-h-9 flex items-center justify-between"
      >
        <div className="flex-1 min-w-0">
          {parsedLink ? (
            renderLink(parsedLink.url)
          ) : (
            <Text variant="bodyTertiary">{placeholder}</Text>
          )}
        </div>
        {parsedLink && renderLinkActions(parsedLink.url)}
      </div>
    );
  }

  const renderLegacy = () => (
    <div className={"space-y-2 pl-2"}>
      <div
        className="relative flex items-center gap-2"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex-1 min-w-0">
          {parsedLink ? (
            <div className="flex flex-wrap gap-2 min-h-[36px] py-2">
              <button
                onClick={() => handleLinkClick(parsedLink.url)}
                className="inline-flex items-center gap-1 max-w-full text-sm text-link hover:text-link-hover transition-colors cursor-pointer hover:underline"
              >
                <span
                  className={cn(
                    "truncate",
                    alwaysShowEditButton ? "max-w-full" : "max-w-[200px]"
                  )}
                >
                  {parsedLink.url}
                </span>
              </button>
            </div>
          ) : (
            <Text variant="bodySecondary">{placeholder}</Text>
          )}
        </div>

        {!readOnly && (
          <Popover open={isOpen} onOpenChange={handlePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="iconNoBg"
                className={cn(
                  "shrink-0 transition-opacity duration-200 w-6 h-6",
                  alwaysShowEditButton || isHovered
                    ? "opacity-100"
                    : "opacity-0"
                )}
              >
                <IconContainer size="sm">
                  <LinkIcon />
                </IconContainer>
              </Button>
            </PopoverTrigger>

            <PopoverContent align="end" padding={16} className="w-80">
              <div className="space-y-3">
                <div>
                  <Text variant="bodySecondary" className="text-sm mb-1">
                    {t("textEditor.linkPopover.enterLink")}
                  </Text>
                  <Input
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("textEditor.linkPopover.linkPlaceholder")}
                    className="w-full"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="secondary" onClick={handleCancel}>
                    {t("cancel")}
                  </Button>
                  <Button onClick={handleSave} disabled={!isValidUrl(linkUrl)}>
                    {t("textEditor.linkPopover.save")}
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );

  // The whole row is the trigger and carries the combobox semantics, as
  // MultiSelect's does. Copy and open are real buttons, tabbed after the
  // trigger rather than in place of it, and each stops propagation on click so
  // pressing one does not also toggle the popover (see the note on their
  // group-focus-within visibility on renderLinkActions).
  const renderNew = () => (
    <Popover open={isOpen} onOpenChange={handlePopoverOpen}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          tabIndex={0}
          data-test="clickable-url-trigger"
          onKeyDown={(keyEvent) => {
            if (keyEvent.target !== keyEvent.currentTarget) return;
            if (keyEvent.key === "Enter" || keyEvent.key === " ") {
              keyEvent.preventDefault();
              keyEvent.currentTarget.click();
            }
          }}
          className="group rounded-lg w-full pl-2 pr-[6px] py-1 min-h-9 flex items-center justify-between border-[0.5px] border-transparent hover:bg-surface-hover-soft cursor-pointer transition-colors duration-200 ease-in-out"
        >
          <div className="flex-1 min-w-0">
            {parsedLink ? (
              renderLink(parsedLink.url)
            ) : (
              <Text variant="bodyTertiary">{placeholder}</Text>
            )}
          </div>
          {parsedLink ? (
            renderLinkActions(parsedLink.url)
          ) : (
            <IconContainer
              size="5"
              color="tertiary"
              className="flex-shrink-0 transition-all duration-150 opacity-0 group-hover:opacity-100 -translate-x-[2px]"
            >
              <Link size={20} strokeWidth={1.5} />
            </IconContainer>
          )}
        </div>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)]"
        // The override stops Radix focusing the content wrapper, which would
        // otherwise scroll-jump; the input below carries `autoFocus` so a
        // keyboard user who opened this with Enter still lands in it. Without
        // both halves the popover opens with nothing focused and the user has
        // to tab the whole page to reach the portalled editor. MultiSelect and
        // SingleSelect pair them the same way.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="space-y-2">
          <Text variant="bodySecondary" className="text-sm mb-1">
            {t("textEditor.linkPopover.enterLink")}
          </Text>
          <div className="flex items-center gap-1 w-full">
            <Input
              autoFocus
              value={linkUrl}
              onChange={(e) => {
                setLinkUrl(e.target.value);
                setTouched(true);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("textEditor.linkPopover.linkPlaceholder")}
              className="w-full"
              containerClassName="flex-1"
            />
            {parsedLink && (
              <IconButton
                size="lg"
                variant="iconNoBg"
                type="button"
                aria-label={t("delete")}
                onClick={() => {
                  onChange("");
                  handleCancel();
                }}
              >
                <IconContainer color="secondary">
                  <Delete />
                </IconContainer>
              </IconButton>
            )}
          </div>
          {urlError && <Text variant="smBodyError">{urlError}</Text>}
        </div>
      </PopoverContent>
    </Popover>
  );

  return isNewFieldsUI ? renderNew() : renderLegacy();
};

export default ClickableUrlField;
